/**
 * RadioTelephonyLicenseModal.jsx - Wave 2B Item 4B
 * Modal for adding/editing Pilot Radio Telephony licenses.
 */

import React, { useState, useEffect } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Radio, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const RadioTelephonyLicenseModal = ({
  open,
  onOpenChange,
  pilotId,
  license = null,
  onSuccess,
}) => {
  const { t, language } = useLanguage();
  const tr = t.radioTelephony || {};
  const isEdit = !!license;
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    license_type: "CZ_GP",
    license_number: "",
    issued_at: "",
    expires_at: "",
    issuer: "ÚCL ČR",
    languages: ["CS"],
    notes: "",
  });

  useEffect(() => {
    if (license) {
      setFormData({
        license_type: license.license_type || "CZ_GP",
        license_number: license.license_number || "",
        issued_at: license.issued_at?.split("T")[0] || "",
        expires_at: license.expires_at?.split("T")[0] || "",
        issuer: license.issuer || "ÚCL ČR",
        languages: license.languages || ["CS"],
        notes: license.notes || "",
      });
    } else {
      setFormData({
        license_type: "CZ_GP",
        license_number: "",
        issued_at: "",
        expires_at: "",
        issuer: "ÚCL ČR",
        languages: ["CS"],
        notes: "",
      });
    }
  }, [license, open]);

  const handleLanguageToggle = (lang) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter((l) => l !== lang)
        : [...prev.languages, lang],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.license_number.trim()) {
      toast.error(tr.licenseNumberRequired || "License number is required");
      return;
    }
    if (!formData.issued_at) {
      toast.error(tr.issueDateRequired || "Issue date is required");
      return;
    }
    if (!formData.expires_at) {
      toast.error(tr.expiryDateRequired || "Expiry date is required");
      return;
    }
    if (formData.languages.length === 0) {
      toast.error(tr.languageRequired || "At least one language is required");
      return;
    }

    setLoading(true);

    try {
      if (isEdit) {
        await api.patch(
          `/pilots/${pilotId}/radio-telephony/${license.id}`,
          formData
        );
        toast.success(tr.licenseUpdated || "RT license updated");
      } else {
        await api.post(`/pilots/${pilotId}/radio-telephony`, formData);
        toast.success(tr.licenseAdded || "RT license added");
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg =
        language === "cs" ? detail?.message_cs : detail?.message_en || detail;
      toast.error(msg || (tr.saveFailed || "Failed to save license"));
    } finally {
      setLoading(false);
    }
  };

  const licenseTypes = [
    { value: "CZ_GP", label: "CZ GP-RT (Všeobecný radiotelefonista)" },
    { value: "EN_RT", label: "EN RT (English RT)" },
    { value: "ICAO_GENERAL", label: "ICAO General RT" },
    { value: "OTHER", label: tr.other || "Other" },
  ];

  const issuers = [
    { value: "ÚCL ČR", label: "ÚCL ČR" },
    { value: "CAA UK", label: "CAA UK" },
    { value: "EASA", label: "EASA" },
    { value: "FAA", label: "FAA" },
    { value: "Other", label: tr.other || "Other" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="rt-license-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Radio className="h-5 w-5" />
            {isEdit
              ? tr.editLicense || "Edit RT License"
              : tr.addLicense || "Add RT License"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* License Type */}
          <div className="space-y-2">
            <Label>{tr.licenseType || "License Type"} *</Label>
            <Select
              value={formData.license_type}
              onValueChange={(value) =>
                setFormData({ ...formData, license_type: value })
              }
            >
              <SelectTrigger data-testid="rt-license-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {licenseTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* License Number */}
          <div className="space-y-2">
            <Label htmlFor="license_number">
              {tr.licenseNumber || "License Number"} *
            </Label>
            <Input
              id="license_number"
              value={formData.license_number}
              onChange={(e) =>
                setFormData({ ...formData, license_number: e.target.value })
              }
              placeholder="e.g. GP-2025-001234"
              data-testid="rt-license-number"
            />
          </div>

          {/* Issuer */}
          <div className="space-y-2">
            <Label>{tr.issuer || "Issuing Authority"} *</Label>
            <Select
              value={formData.issuer}
              onValueChange={(value) =>
                setFormData({ ...formData, issuer: value })
              }
            >
              <SelectTrigger data-testid="rt-license-issuer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {issuers.map((issuer) => (
                  <SelectItem key={issuer.value} value={issuer.value}>
                    {issuer.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="issued_at">
                {tr.issuedAt || "Issue Date"} *
              </Label>
              <Input
                id="issued_at"
                type="date"
                value={formData.issued_at}
                onChange={(e) =>
                  setFormData({ ...formData, issued_at: e.target.value })
                }
                data-testid="rt-issued-at"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expires_at">
                {tr.expiresAt || "Expiry Date"} *
              </Label>
              <Input
                id="expires_at"
                type="date"
                value={formData.expires_at}
                onChange={(e) =>
                  setFormData({ ...formData, expires_at: e.target.value })
                }
                data-testid="rt-expires-at"
              />
            </div>
          </div>

          {/* Languages */}
          <div className="space-y-2">
            <Label>{tr.languages || "Languages"} *</Label>
            <div className="flex gap-4 p-3 bg-slate-50 rounded-lg border">
              {["CS", "EN", "DE"].map((lang) => (
                <div key={lang} className="flex items-center gap-2">
                  <Checkbox
                    id={`lang-${lang}`}
                    checked={formData.languages.includes(lang)}
                    onCheckedChange={() => handleLanguageToggle(lang)}
                    data-testid={`rt-lang-${lang}`}
                  />
                  <Label
                    htmlFor={`lang-${lang}`}
                    className="font-normal cursor-pointer"
                  >
                    {lang}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              {tr.languagesHint ||
                "Select languages for which the RT license is valid"}
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{tr.notes || "Notes"}</Label>
            <Input
              id="notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder={tr.notesPlaceholder || "Optional notes..."}
              data-testid="rt-notes"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t.actions?.cancel || "Cancel"}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-primary hover:bg-primary/90"
              data-testid="rt-submit-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {isEdit
                ? t.actions?.update || "Update"
                : t.actions?.create || "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RadioTelephonyLicenseModal;
